import { useState, useCallback, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Divider, Paper } from '@mui/material'
import { ListItem, List, ListItemIcon, Fade } from '@material-ui/core'
import { Info, DeleteOutlined } from '@mui/icons-material'

import { getMe } from '@tabeeb/modules/account/selectors'
import UserQualificationsGrid from '@tabeeb/modules/qualifications/components/UserQualificationsGrid'
import { useDialogState, useQuery } from '@tabeeb/modules/shared/utils/hooks'
import { CertificatesDisplayType } from '@tabeeb/modules/certificates/constants'
import { Grid, PageLayout } from '@collaborate-health/uikit'
import { openPoliciesViewer } from '../../../policies/actions'

import DeleteAccountDialog from '../DeleteAccountDialog'
import MainSettings from '../mainSettings'
import NotificationSettings from '../NotificationSettings'
import Address from '../address'
import HealthStatisticsContainer from '../../../healthData/containers/HealthStatisticsContainer'
import { UserSettingsTabs, getTabs } from '../../helper'

import Avatar from '../avatar'
import CertificatesGrid from '../../../certificates/components/CertificatesGrid'

const UserSettings = ({ showTabs, showDeleteAccountButton }) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(getMe)
  const query = useQuery()

  const [selectedTab, setSelectedTab] = useState(UserSettingsTabs.ProfileDetails)

  useEffect(() => {
    const queryTab = query.get('tab')
    if (queryTab) {
      setSelectedTab(queryTab)
    }
  }, [query])

  const handleSelectTab = useCallback(
    (value) => () => {
      setSelectedTab(value)
    },
    []
  )

  const handleOpenPoliciesViewer = useCallback(() => {
    dispatch(openPoliciesViewer())
  }, [dispatch])

  const [deleteAccountDialogOpen, onDeleteAccountDialogOpen, onDeleteAccountDialogClose] = useDialogState()

  const tabs = useMemo(() => getTabs(showTabs), [showTabs])

  return (
    <PageLayout
      component={Paper}
      disableOuterPadding={false}
      variant='outlined'
      spacing='page'
      maxWidth='xl'
      background='default'
      fullHeight
    >
      <Grid container spacing={2} flexGrow={1} pt={1}>
        <Grid item xs={4} md={5} lg={3}>
          <Grid container direction='column' spacing={1}>
            <Grid item>
              <Avatar />
            </Grid>
            <Grid item>
              <List>
                {tabs.map(
                  (item) =>
                    item.toDisplay && (
                      <>
                        {item.displayDividerBefore && (
                          <div style={{ padding: '4px' }}>
                            <Divider sx={{ height: 0 }} />
                          </div>
                        )}
                        <ListItem
                          key={item.id}
                          selected={selectedTab === item.id}
                          button
                          onClick={handleSelectTab(item.id)}
                          {...(item.url && {
                            component: 'a',
                            href: item.url,
                            target: '_blank',
                            onClick: () => {},
                          })}
                        >
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          {item.label}
                        </ListItem>
                      </>
                    )
                )}
              </List>
            </Grid>
            <Grid item>
              <Divider sx={{ height: 0 }} />
            </Grid>
            <Grid item>
              <List>
                <ListItem button onClick={handleOpenPoliciesViewer}>
                  <ListItemIcon>
                    <Info />
                  </ListItemIcon>
                  View policies
                </ListItem>
                {showDeleteAccountButton && (
                  <ListItem button onClick={onDeleteAccountDialogOpen}>
                    <ListItemIcon>
                      <DeleteOutlined />
                    </ListItemIcon>
                    Delete profile
                  </ListItem>
                )}
              </List>
            </Grid>
          </Grid>
        </Grid>
        <Fade in>
          <Grid item xs={8} md={7} lg={9} display='flex' flexDirection='column'>
            {selectedTab === UserSettingsTabs.ProfileDetails && <MainSettings />}
            {selectedTab === UserSettingsTabs.Address && <Address />}

            {selectedTab === UserSettingsTabs.Notifications && <NotificationSettings />}
            {selectedTab === UserSettingsTabs.HealthData && <HealthStatisticsContainer />}
            {selectedTab === UserSettingsTabs.Certificates && (
              <CertificatesGrid user={currentUser} displayType={CertificatesDisplayType.Personal} />
            )}
            {selectedTab === UserSettingsTabs.Qualifications && (
              <UserQualificationsGrid simpleView user={currentUser} />
            )}
          </Grid>
        </Fade>
      </Grid>
      <DeleteAccountDialog open={deleteAccountDialogOpen} onClose={onDeleteAccountDialogClose} />
    </PageLayout>
  )
}

UserSettings.propTypes = {
  showTabs: PropTypes.shape({
    showHealthData: PropTypes.bool.isRequired,
    showCertificates: PropTypes.bool.isRequired,
    showQualifications: PropTypes.bool.isRequired,
  }).isRequired,
  showDeleteAccountButton: PropTypes.bool.isRequired,
}

export default UserSettings
